/**
 * DEV-ONLY mock fixtures — the demo identity + a sample user per role/scope shape. Consumed only by
 * the mock API layer (src/ui/src/dev/mock.ts) for offline UI review; nothing here ships to prod
 * (the mock module is tree-shaken out of production builds). Auth in every real environment is
 * Microsoft Entra SSO (ADR-0007).
 */
import type {Role} from '@ait-tracker/shared';
import type {SessionUser} from '../lib/api';

/** The identity the mock layer resolves as (an admin, so the mock admin console is exercised). */
export const DEMO_EMAIL = 'demo-user@coxautoinc.com';
export const DEMO_USER: SessionUser = {
  email: DEMO_EMAIL,
  displayName: 'Demo Admin',
  roles: ['admin'],
  assignedPortfolios: [],
  assignedDomainIds: []
};

const r = (...roles: Role[]): Role[] => roles;

export interface RolePreview {
  id: string;
  label: string;
  /** The effective user the UI renders as while this preview is active. */
  user: SessionUser;
}

/**
 * Sample users spanning each role + scope shape, so the mock admin console (listUsers) is populated
 * for offline review.
 */
export const ROLE_PREVIEWS: RolePreview[] = [
  {
    id: 'viewer-growth',
    label: 'Portfolio Viewer · CADS portfolio',
    user: {
      email: 'growth.viewer@coxautoinc.com',
      displayName: 'Growth Viewer',
      roles: r('portfolio-viewer'),
      assignedPortfolios: ['cads'],
      assignedDomainIds: []
    }
  },
  {
    id: 'viewer-platform',
    label: 'Portfolio Viewer · ETE portfolio',
    user: {
      email: 'platform.viewer@coxautoinc.com',
      displayName: 'Platform Viewer',
      roles: r('portfolio-viewer'),
      assignedPortfolios: ['ete'],
      assignedDomainIds: []
    }
  },
  {
    id: 'viewer-combo',
    label: 'Portfolio Viewer · CADS + an extra domain',
    user: {
      email: 'combo.viewer@coxautoinc.com',
      displayName: 'Combo Viewer',
      roles: r('portfolio-viewer'),
      assignedPortfolios: ['cads'],
      assignedDomainIds: ['13'] // "Service Desk" — domains are keyed by the file's stable id, not a slug
    }
  },
  {
    id: 'leader-multi',
    label: 'Portfolio Viewer · ETE + CADS portfolios',
    user: {
      email: 'multi.leader@coxautoinc.com',
      displayName: 'Multi Leader',
      roles: r('portfolio-viewer'),
      assignedPortfolios: ['ete', 'cads'],
      assignedDomainIds: []
    }
  },
  {
    id: 'viewer-empty',
    label: 'Portfolio Viewer · no scope (empty)',
    user: {
      email: 'empty.viewer@coxautoinc.com',
      displayName: 'Empty Viewer',
      roles: r('portfolio-viewer'),
      assignedPortfolios: [],
      assignedDomainIds: []
    }
  },
  {
    id: 'owner',
    label: 'Domain Owner · specific domains',
    user: {
      email: 'demo.owner@coxautoinc.com',
      displayName: 'Demo Owner',
      roles: r('domain-owner'),
      assignedPortfolios: [],
      assignedDomainIds: ['13'] // "Service Desk" — domains are keyed by the file's stable id, not a slug
    }
  }
];
